import React, { useMemo } from 'react';
import { Form, Switch } from 'antd';

interface SelectWithAllToggleProps {
	label: string;
	name: string; // field name, e.g. "problemTypeIds"
	allName: string; // toggle name, e.g. "allProblemTypes"
	form: any; // AntD Form instance
	defaultFormFieldStyle?: React.CSSProperties;
	children: React.ReactNode; // pass field from parent
	displaySelectAllWithLabel?: boolean;
	required?: boolean;
	requiredIfNotAllSelected?: Boolean;
	disabled?: boolean;
}

const SelectWithAllToggle: React.FC<SelectWithAllToggleProps> = ({
	label,
	name,
	allName,
	form,
	defaultFormFieldStyle = {},
	children,
	displaySelectAllWithLabel = false,
	required = false,
	requiredIfNotAllSelected = false,
	disabled = false,
}) => {
	const selectedIds = Form.useWatch(name, form);
	const allSelected = Form.useWatch(allName, form);

	const allSelectedField = useMemo(
		() =>
			disabled ? null : (
				<div className="flex flex-row items-center">
					<span className="mr-2">Select All</span>
					<Form.Item name={allName} valuePropName="checked" noStyle>
						<Switch defaultChecked={false} size="small" />
					</Form.Item>
				</div>
			),
		[allName, disabled]
	);

	return (
		<Form.Item label={displaySelectAllWithLabel ? null : label} required={required}>
			<div className="flex flex-col" style={defaultFormFieldStyle}>
				{displaySelectAllWithLabel ? (
					<div className="mb-2 flex flex-row items-center justify-between">
						<span>{label}</span>
						{allSelectedField}
					</div>
				) : (
					<div className="mb-2 flex w-full flex-row items-center justify-between text-sm text-black/85">
						<div>{`Selected: ${allSelected ? 'All' : `(${(selectedIds || []).length})`}`}</div>
						{allSelectedField}
					</div>
				)}

				<Form.Item
					name={name}
					noStyle
					rules={[
						{
							type: 'array',
							required: requiredIfNotAllSelected && !allSelected,
							message: `Please select a ${label}`,
						},
					]}
				>
					{React.isValidElement(children)
						? React.cloneElement(children, {
								disabled: allSelected || disabled,
						  })
						: children}
				</Form.Item>
			</div>
		</Form.Item>
	);
};

export default SelectWithAllToggle;
